import React, { useRef, useState, forwardRef, useEffect } from "react";
import ReactQuill from "react-quill";
//import Quill from "quill";
//type RangeStatic = { index: number; length: number } | null;
import "react-quill/dist/quill.snow.css";

//type CustomFieldKey = "ata" | "customerNumber" | "containerNumber" | "notes" | "positionsNumber";
import type { EditableOrderField } from "../types/index";
import { useTranslation } from "react-i18next";

interface CustomQuillEditorProps {
    value: string;
    onChange: (val: string) => void;
    onFieldValueSelected: (field: EditableOrderField, value: string) => void;
    style?: React.CSSProperties;
    isReadOnlyMode: boolean;
}

const CustomQuillEditor = forwardRef<ReactQuill, CustomQuillEditorProps>(
    ({ value, onChange, onFieldValueSelected, style, isReadOnlyMode }, ref) => {
        const quillRef = useRef<ReactQuill | null>(null);
        const [showPopup, setShowPopup] = useState(false);
        const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
        const { t } = useTranslation();

        // ? Prevent jumping to top on paste
        useEffect(() => {
            const quill = quillRef.current?.getEditor();
            if (!quill) return;

            const container = quill.root;

            const handlePaste = () => {
                if (!container) return;
                const scrollTop = container.scrollTop;

                // wait for Quill to insert content
                setTimeout(() => {
                    container.scrollTop = scrollTop; // restore where user was
                }, 0);
            };

            quill.root.addEventListener("paste", handlePaste);
            return () => quill.root.removeEventListener("paste", handlePaste);
        }, []);

        const handleSelectionChange = (range: { index: number; length: number } | null) => {
            if (range && range.length > 0 && quillRef.current && isReadOnlyMode) {
                const quill = quillRef.current.getEditor();
                const bounds = quill.getBounds(range.index, range.length);

                if (bounds) {
                    const popupHeight = 60; // approximate height of popup
                    const top = bounds.top - popupHeight - 10; // show above with padding
                    const left = 0; // bounds.left;

                    setPopupPosition({ top: Math.max(top, 0), left }); // prevent offscreen top
                    setShowPopup(true);
                } else {
                    setShowPopup(false);
                }
            } else {
                setShowPopup(false);
                }

        };



        const handleOptionClick = (field: EditableOrderField) => {
            const quill = quillRef.current?.getEditor();
            const range = quill?.getSelection();
            let selectedText = "";
            if (range && quill) {
                selectedText = quill.getText(range.index, range.length).trim();
            }

            if (selectedText) {
                    onFieldValueSelected(field, selectedText);
            }
            setShowPopup(false);
        };

        return (
            <div style={{ position: "relative", ...style }}>
                <ReactQuill
                    ref={(instance) => {
                        quillRef.current = instance;
                        if (typeof ref === "function") ref(instance);
                        else if (ref) (ref as React.MutableRefObject<ReactQuill | null>).current = instance;
                    }}
                    value={value}
                    onChange={onChange}
                    onChangeSelection={handleSelectionChange}
                    theme="snow"
                />
                {showPopup && (
                    <div
                        style={{
                            position: "absolute",
                            top: popupPosition.top,
                            left: popupPosition.left,
                            background: "white",
                            border: "1px solid #ccc",
                            padding: "10px",
                            borderRadius: "4px",
                            zIndex: 1000,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            display: "flex",
                            flexDirection: "column", // stack buttons vertically
                            gap: "6px", // adds spacing between buttons
                            ...(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
                                ? {
                                    background: "#2b2b2b",
                                    color: "#fff",
                                    border: "1px solid #555",
                                }
                                : {}),
                        }}
                    >
                        <button onClick={() => {
                            const quill = quillRef.current?.getEditor();
                            const range = quill?.getSelection();
                            let selectedText = "";
                            if (range && quill) {
                                selectedText = quill.getText(range.index, range.length).trim();
                            }
                            navigator.clipboard.writeText(selectedText);
                             setShowPopup(false);
                        }}>{t("Copy")}</button>
                        <button onClick={() => handleOptionClick("ata")}>{t("ATA")}</button>
                        <button onClick={() => handleOptionClick("customerNumber")}>{t("Customer Reference Number Short")}</button>
                        <button onClick={() => handleOptionClick("containerNumber")}>{t("Container number Short")}</button>
                        <button onClick={() => handleOptionClick("notes")}>{t("Notes")}</button>
                        <button onClick={() => handleOptionClick("positionsNumber")}>{t("Position number Short")}</button>
                        <button onClick={() => handleOptionClick("atc")}>{t("ATC")}</button>
                        <button onClick={() => handleOptionClick("mrn")}>{t("ATC")}</button>
                    </div>
                )}
            </div>
        );
    }
);

export default CustomQuillEditor;
